import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable8 = ({ data }) => {
  const pdfRef = useRef();

  const years = data.map((item) => item.year);
  const employment = data.map((item) => Number(item.employment));
  const growthRates = data.map((item) =>
    item.growth_rate ? Number(item.growth_rate) : null
  );

  // ---- Summary Analytics ----
  const first = employment[0];
  const last = employment[employment.length - 1];
  const growthPercent = (((last - first) / first) * 100).toFixed(1);

  const peakValue = Math.max(...employment);
  const peakYear = years[employment.indexOf(peakValue)];

  const latestGrowthRate = growthRates[growthRates.length - 1];

  // ---- Chart ----
  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Employment Count",
        data: employment,
        borderColor: "#B5EAD6",
        backgroundColor: "#B5EAD630",
        borderWidth: 3,
        tension: 0.4,
        yAxisID: "y",
        pointRadius: 6,
        pointHoverRadius: 8,
      },
      {
        label: "Growth Rate (%)",
        data: growthRates,
        borderColor: "#999933",
        backgroundColor: "#99993330",
        borderWidth: 3,
        tension: 0.4,
        yAxisID: "y1",
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Employment & Growth Rate (Age Group: 25–34)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      y: {
        title: { display: true, text: "Employment" },
        beginAtZero: true,
      },
      y1: {
        position: "right",
        title: { display: true, text: "Growth Rate (%)" },
        grid: { drawOnChartArea: false },
      },
    },
  };

  // ---- PDF Export ----
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#fff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const usableWidth = pageWidth - 10;
      const imgHeight = (canvas.height * usableWidth) / canvas.width;

      // Header
      pdf.addImage(utahData, "PNG", 60, 8, 30, 18);
      pdf.setFontSize(20);
      pdf.text("UTahData.org", 100, 22);
      pdf.line(10, 33, 200, 33);

      // Body
      pdf.addImage(img, "PNG", 5, 35, usableWidth, imgHeight);

      // Footer
      pdf.line(10, 260, 200, 260);
      pdf.addImage(utahData, "PNG", 22, 265, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, 275);
      pdf.addImage(bodex, "PNG", 68, 269, 44, 10);
      pdf.text("using", 112, 275);
      pdf.addImage(kpi, "PNG", 125, 265, 20, 20);

      pdf.text("20250916.v3.198", 150, 275);
      pdf.text(`Downloaded: ${new Date().toLocaleDateString()}`, 140, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("age25-34_employment_growth.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          Age Group 25–34: Employment & Growth Trend
        </h2>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Overall Growth (2018–2024)
            </p>
            <p className="text-2xl font-bold text-blue-800">
              +{growthPercent}%
            </p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Peak Year</p>
            <p className="text-2xl font-bold text-yellow-800">{peakYear}</p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Latest Growth</p>
            <p className="text-2xl font-bold text-green-800">
              {latestGrowthRate ? `${latestGrowthRate}%` : "N/A"}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-center shadow-md border rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Year</th>
                  <th className="p-2">Employment</th>
                  <th className="p-2">Growth Rate (%)</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr
                    key={row.year}
                    className={`${
                      row.year === 2024
                        ? "bg-green-100 font-bold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2">{row.year}</td>
                    <td className="p-2">
                      {Number(row.employment).toLocaleString("en-US")}
                    </td>
                    <td className="p-2">{row.growth_rate || "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="Download" />
        </button>
      </div>
    </>
  );
};

export default EngTable8;
